/**
 * ProbeStatusCard — Surfaces the scraper's CaleProcure health probe state
 * and its graduated-resumption machinery, all in one place.
 *
 * Data source: GET /api/scraper/health/full (aggregates scraper_config.
 * caleprocure_access + throttle_state + coverage_scheduler + recent alerts
 * + suspect-jobs stats).
 *
 * Reuses SchedulerHealthCard's .schcard CSS for visual consistency.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity, AlertTriangle, CheckCircle, Clock, Loader2, Pause, Play,
  ShieldAlert, ShieldCheck, ChevronDown, ChevronRight, FlaskConical, Zap,
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './SchedulerHealthCard.css';

function timeAgo(iso) {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0) return 'just now';
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function severityColor(sev) {
  if (sev === 'critical') return 'is-bad';
  if (sev === 'error') return 'is-bad';
  if (sev === 'warning') return 'is-warn';
  return 'is-muted';
}

export default function ProbeStatusCard() {
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [gradOpen, setGradOpen] = useState(true);

  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ['scraper', 'health-full'],
    queryFn: () => apiClient.get('/api/scraper/health/full'),
    refetchInterval: 15000,
  });

  if (isLoading) {
    return (
      <div className="schcard schcard--loading">
        <Loader2 size={16} className="schcard-spin" />
        <span>Loading probe status…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="schcard schcard--error">
        <AlertTriangle size={16} />
        <span>Probe status unreachable: {error.message}</span>
      </div>
    );
  }

  const probe = data?.probe;
  const grad = data?.graduation;
  const throttle = data?.throttle;
  const sched = data?.coverage_scheduler;
  const alerts = data?.alerts_recent || [];
  const suspect = data?.suspect_jobs || { total: 0, awaiting_retry: 0, retried: 0 };

  const phase = probe?.phase || 'unknown';
  const accessible = !!probe?.accessible;

  // Derive overall card state + label
  let state, stateLabel, StateIcon;
  if (phase === 'resumed') {
    state = 'running';
    stateLabel = 'RESUMED';
    StateIcon = ShieldCheck;
  } else if (phase === 'testing') {
    state = 'running';
    stateLabel = 'TESTING (graduation)';
    StateIcon = FlaskConical;
  } else if (accessible) {
    state = 'waiting';
    stateLabel = 'ACCESSIBLE (paused)';
    StateIcon = Clock;
  } else if (probe?.last_waf_challenge || probe?.scraper_waf_observations > 0) {
    state = 'throttled';
    stateLabel = 'BLOCKED (WAF)';
    StateIcon = ShieldAlert;
  } else {
    state = 'drift';
    stateLabel = 'BLOCKED';
    StateIcon = AlertTriangle;
  }

  const blockedMs = probe?.first_failure_at
    ? Date.now() - new Date(probe.first_failure_at).getTime()
    : null;
  const blockedFor = blockedMs != null
    ? (blockedMs < 3600000
        ? `${Math.floor(blockedMs / 60000)}m`
        : `${(blockedMs / 3600000).toFixed(1)}h`)
    : null;

  // Nudges — surfaces the "why" of the current state
  const nudges = [];
  if (phase === 'testing' && grad?.tests) {
    const passed = grad.tests.filter((t) => t.status === 'passed').length;
    const total = grad.tests.length;
    nudges.push(`Graduation in progress — ${passed}/${total} tests passed. Pipeline will auto-resume if all pass.`);
  }
  if (throttle?.blocked && throttle?.blocked_by === 'health-probe') {
    nudges.push('Worker queue paused by health-probe until CaleProcure is verified accessible.');
  }
  if (probe?.last_waf_challenge) {
    nudges.push(`Probe observed WAF challenge: ${probe.last_waf_challenge}`);
  }
  if (probe?.last_scraper_waf_at && Date.now() - new Date(probe.last_scraper_waf_at).getTime() < 60 * 60 * 1000) {
    nudges.push('Scraper saw WAF in last 60 min — quarantine extended; scheduler will not auto-resume yet.');
  }
  if (suspect.awaiting_retry > 0 && phase === 'resumed') {
    nudges.push(`${suspect.awaiting_retry} suspect jobs still awaiting retry — will enqueue on next recovery.`);
  }
  // Slow-start ramp nudge — surfaces to the operator that the scheduler
  // is deliberately running with a larger cooldown right now so they
  // don't misread the slower cadence as a fresh stall.
  if (sched?.enabled && sched?.paused_by == null) {
    const rampUntil = data?.coverage_scheduler?.ramp_until || null;
    // Note: ramp_until isn't in the base response shape; look up via config updated_at as proxy.
    // For now, derive from auto_resumed_at within last hour.
    const resumedAt = sched?.auto_resumed_at;
    if (resumedAt) {
      const sinceResumeMin = Math.floor((Date.now() - new Date(resumedAt).getTime()) / 60000);
      if (sinceResumeMin < 60) {
        nudges.push(`Slow-start active — ${60 - sinceResumeMin}min until normal cadence (prevents CF re-block cascade).`);
      }
    }
  }

  return (
    <div className={`schcard schcard--${state}`}>
      <div className="schcard-header">
        <div className="schcard-header-left">
          <Activity size={16} className="schcard-icon" />
          <span className="schcard-title">CaleProcure Health Probe</span>
        </div>
        <div className={`schcard-badge schcard-badge--${state}`}>
          <StateIcon size={12} />
          <span>{stateLabel}</span>
        </div>
      </div>

      {nudges.length > 0 && (
        <div className="schcard-nudges">
          {nudges.map((n, i) => (
            <div key={i} className="schcard-nudge">
              <AlertTriangle size={12} />
              <span>{n}</span>
            </div>
          ))}
        </div>
      )}

      <div className="schcard-grid">
        <div className="schcard-metric">
          <span className="schcard-metric-label">Phase</span>
          <span className="schcard-metric-value">{phase}</span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Probe says</span>
          <span className={`schcard-metric-value ${accessible ? 'is-good' : 'is-bad'}`}>
            {accessible ? 'accessible' : 'blocked'}
          </span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Last check</span>
          <span className="schcard-metric-value">{timeAgo(probe?.last_checked)}</span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Last reason</span>
          <span className="schcard-metric-value" title={probe?.last_check_reason || ''}>
            {(probe?.last_check_reason || '—').slice(0, 28)}
          </span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Clean streak</span>
          <span className={`schcard-metric-value ${probe?.consecutive_successes >= 3 ? 'is-good' : 'is-muted'}`}>
            {probe?.consecutive_successes || 0} / 3 for grad
          </span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Failure streak</span>
          <span className={`schcard-metric-value ${(probe?.consecutive_failures || 0) > 0 ? 'is-warn' : ''}`}>
            {probe?.consecutive_failures || 0}
          </span>
        </div>
        {blockedFor && (
          <div className="schcard-metric">
            <span className="schcard-metric-label">Blocked for</span>
            <span className="schcard-metric-value is-warn">{blockedFor}</span>
          </div>
        )}
        <div className="schcard-metric">
          <span className="schcard-metric-label">Coverage scheduler</span>
          <span className={`schcard-metric-value ${sched?.enabled ? 'is-good' : 'is-muted'}`}>
            {sched?.enabled ? 'enabled' : (sched?.paused_by || 'paused')}
          </span>
        </div>
        <div className="schcard-metric">
          <span className="schcard-metric-label">Suspect jobs</span>
          <span className={`schcard-metric-value ${suspect.awaiting_retry > 0 ? 'is-warn' : 'is-muted'}`}>
            {suspect.awaiting_retry} pending retry / {suspect.total} total
          </span>
        </div>
      </div>

      {/* Graduation progress — only show when there's graduation state to display */}
      {grad?.tests && grad.tests.length > 0 && (
        <div className="schcard-section">
          <button
            className="schcard-section-toggle"
            onClick={() => setGradOpen(!gradOpen)}
            type="button"
          >
            {gradOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <span>Graduation tests ({grad.tests.length})</span>
          </button>
          {gradOpen && (
            <div className="schcard-sublist">
              {grad.tests.map((t, i) => {
                const statusClass = t.status === 'passed' ? 'is-good'
                  : t.status === 'pending' ? 'is-warn'
                  : t.status === 'waiting' ? 'is-muted'
                  : 'is-bad';
                const Icon = t.status === 'passed' ? CheckCircle
                  : t.status === 'pending' ? Loader2
                  : t.status === 'waiting' ? Pause
                  : AlertTriangle;
                return (
                  <div key={i} className={`schcard-sublist-item ${statusClass}`}>
                    <Icon size={12} className={t.status === 'pending' ? 'schcard-spin' : ''} />
                    <span>
                      Test {i + 1}: {t.params?.dept} {t.params?.acq}{' '}
                      {t.params?.from}→{t.params?.to} (expect ~{t.params?.expectedPOs})
                    </span>
                    <span className="schcard-sublist-right">
                      {t.status}
                      {t.result?.uniquePOs != null && ` · ${t.result.uniquePOs} POs`}
                    </span>
                  </div>
                );
              })}
              {grad.aborted_reason && (
                <div className="schcard-nudge">
                  <AlertTriangle size={12} />
                  <span>Aborted: {grad.aborted_reason}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Recent alerts */}
      {alerts.length > 0 && (
        <div className="schcard-section">
          <button
            className="schcard-section-toggle"
            onClick={() => setAlertsOpen(!alertsOpen)}
            type="button"
          >
            {alertsOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <span>Recent probe alerts ({alerts.length})</span>
          </button>
          {alertsOpen && (
            <div className="schcard-sublist">
              {alerts.slice(0, 10).map((a, i) => (
                <div key={i} className={`schcard-sublist-item ${severityColor(a.severity)}`}>
                  <Zap size={12} />
                  <span title={a.message}>
                    <strong>{a.kind}</strong> — {(a.message || '').slice(0, 80)}
                  </span>
                  <span className="schcard-sublist-right">{timeAgo(a.occurred_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="schcard-footer">
        Polling every 15s · last refreshed {timeAgo(new Date(dataUpdatedAt).toISOString())}
      </div>
    </div>
  );
}
